/**
 * Admin impersonation handoff. The auth worker's Access-gated admin UI mints a
 * short-lived session for a target user (`impersonatedBy` stamped on it) and
 * opens `/impersonate#token=<session token>` here.
 *
 * A URL is not an authorisation. Anyone can send anyone a link, so the token in
 * the fragment is treated as an UNTRUSTED CLAIM until two things hold:
 *
 *  1. it resolves, on the worker, to a session that actually carries
 *     `impersonatedBy` — i.e. one the admin API minted, not an ordinary session
 *     an attacker signed into and copied out of `set-auth-token`; and
 *  2. a human has seen the account it names and said yes.
 *
 * Without that gate `/impersonate#token=…` is one-click session fixation: the
 * planted token outranks the victim's own cookie by design (client.ts attaches
 * it as `Authorization: Bearer`, the proxy forwards it untouched, and the
 * worker rewrites the request cookie from it), and because an ordinary session
 * has no `impersonatedBy`, ImpersonationBanner would render nothing — so
 * everything the victim went on to create would land silently in the
 * attacker's account.
 *
 * This module is the untrusted-input half: read the claim, resolve it, refuse
 * it. The confirmation screen and the localStorage write live in
 * ./components/impersonate-confirm.tsx. Nothing here imports ./client — that
 * module imports SESSION_TOKEN_KEY from here.
 *
 * The token still travels in the URL FRAGMENT: fragments never reach the
 * server, so it stays out of Vercel/proxy logs.
 */

/** Single source of truth for the storage key; client.ts imports it. */
export const SESSION_TOKEN_KEY = 'flashka.ba.session-token';

/** The path the admin UI's "Open app as user" link points at. */
export const IMPERSONATE_PATH = '/impersonate';

/**
 * Same-origin Better Auth path (see client.ts — ONE transport, always). The
 * cookie cache is bypassed so the answer describes the token itself and not a
 * `session_data` cookie the browser happens to be carrying.
 */
const GET_SESSION_URL = '/api/auth/get-session?disableCookieCache=true';

/** The account a handoff token names, once the worker has vouched for it. */
export interface ImpersonationTarget {
  /** Email of the user the token signs you in AS. */
  email: string;
  /** Their display name, when the account has one. */
  name: string | null;
  /** The admin the worker stamped on the session (Access email). */
  impersonatedBy: string;
}

/**
 * `null` when this page load is not a handoff at all — the overwhelmingly
 * common case, and the caller's signal to mount the app normally.
 *
 * Otherwise the claim from the fragment (`token: null` when the link carried
 * none), with the fragment SCRUBBED from the address bar on the way out so the
 * token can't be shoulder-surfed, screenshotted, or resurrected by a refresh.
 * Returning it rather than stashing it in module state keeps the screen below
 * a pure function of its props — and keeps a second call from silently
 * answering `null` under React StrictMode's double render.
 */
export function readImpersonationHandoff(): { token: string | null } | null {
  if (window.location.pathname !== IMPERSONATE_PATH) return null;
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
  window.history.replaceState(null, '', window.location.pathname);
  return { token: token ? token : null };
}

/**
 * Ask the worker who this token is, and REFUSE anything that is not a genuine
 * admin impersonation session.
 *
 * `credentials: 'omit'` is load-bearing, not hygiene: with the cookie attached,
 * a token the worker fails to resolve gets stripped and the request falls back
 * to the visitor's OWN session — so a bogus link would answer with the
 * visitor's account and the screen would offer to "impersonate" them into
 * themselves. Omitting the cookie makes the answer describe the token and
 * nothing else.
 *
 * Refuses on: a non-2xx, an unparseable body, a signed-out answer, a session
 * without `impersonatedBy` (an ordinary session — the fixation case), and a
 * session whose user has no email (the screen's whole job is to NAME the
 * account; it may not ask about one it cannot show).
 *
 * `fetchImpl` is injectable for tests only; production always takes the global.
 */
export async function resolveImpersonationSession(
  token: string,
  fetchImpl: typeof fetch = (...args) => fetch(...args),
): Promise<ImpersonationTarget | null> {
  let body: unknown;
  try {
    const response = await fetchImpl(GET_SESSION_URL, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
      cache: 'no-store',
    });
    if (!response.ok) return null;
    body = await response.json();
  } catch {
    // Offline, worker down, HTML error page — all "we could not verify", which
    // is the same answer as "refused".
    return null;
  }

  const payload = body as {
    session?: { impersonatedBy?: unknown } | null;
    user?: { email?: unknown; name?: unknown } | null;
  } | null;

  const impersonatedBy = payload?.session?.impersonatedBy;
  if (typeof impersonatedBy !== 'string' || impersonatedBy.length === 0) return null;

  const email = payload?.user?.email;
  if (typeof email !== 'string' || email.length === 0) return null;

  const name = payload?.user?.name;
  return {
    email,
    name: typeof name === 'string' && name.length > 0 ? name : null,
    impersonatedBy,
  };
}
